/**
 * CategoryFormModal Component
 *
 * 카테고리 추가/수정을 위한 모달 폼
 *
 * 책임:
 * - 카테고리 입력 폼 제공 (대분류/소분류)
 * - 이모지 선택 UI
 * - 유효성 검증
 * - 생성/수정 구분 처리
 */

import { useState, useEffect } from 'react'
import { EmojiPicker } from './EmojiPicker'
import { Modal } from '@/shared/components/ui/Modal'
import { useToast } from '@/shared/components/ui/Toast'
import type { Category, CategoryType, CategoryLevel } from '../types/Category'

/**
 * CategoryFormModal Props
 */
export interface CategoryFormModalProps {
  /** 모달 표시 여부 */
  show: boolean
  /** 수정할 카테고리 (수정 모드) */
  editingCategory?: Category | null
  /** 부모 카테고리 ID (소분류 추가 시) */
  parentId?: string | null
  /** 카테고리 타입 (수입/지출) */
  type: CategoryType
  /** 대분류 목록 (소분류 추가/수정 시 부모 선택용) */
  majorCategories: Category[]
  /** 닫기 핸들러 */
  onClose: () => void
  /** 저장 핸들러 */
  onSave: (input: Omit<Category, 'id'>) => Promise<void>
}

/**
 * 카테고리 폼 모달
 *
 * 대분류/소분류 추가/수정을 모두 지원
 */
export function CategoryFormModal({
  show,
  editingCategory,
  parentId,
  type,
  majorCategories,
  onClose,
  onSave,
}: CategoryFormModalProps) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [icon, setIcon] = useState('📁')
  const [level, setLevel] = useState<CategoryLevel>('major')
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState(0)
  const [saving, setSaving] = useState(false)

  /**
   * 폼 초기화
   */
  useEffect(() => {
    if (editingCategory) {
      // 수정 모드: 기존 값으로 초기화
      setName(editingCategory.name)
      setIcon(editingCategory.icon)
      setLevel(editingCategory.level)
      setSelectedParentId(editingCategory.parentId)
      setSortOrder(editingCategory.sortOrder)
    } else {
      // 추가 모드: 기본값 설정
      setName('')
      setIcon('📁')
      setLevel(parentId ? 'minor' : 'major')
      setSelectedParentId(parentId || null)
      setSortOrder(0)
    }
  }, [editingCategory, parentId, show])

  /**
   * 저장 핸들러
   */
  const handleSave = async () => {
    // 유효성 검증
    if (!name.trim()) {
      toast.warning('카테고리 이름을 입력해주세요')
      return
    }

    if (level === 'minor' && !selectedParentId) {
      toast.warning('상위 카테고리를 선택해주세요')
      return
    }

    try {
      setSaving(true)
      await onSave({
        name: name.trim(),
        icon,
        type,
        level,
        parentId: level === 'minor' ? selectedParentId : null,
        sortOrder,
        isDefault: false,
        isActive: true,
      })
    } catch (err) {
      console.error('저장 실패:', err)
    } finally {
      setSaving(false)
    }
  }

  /**
   * 모달 제목
   */
  const getTitle = () => {
    if (editingCategory) return '카테고리 수정'
    if (parentId) return '소분류 추가'
    return '대분류 추가'
  }

  return (
    <Modal isOpen={show} onClose={onClose} title={getTitle()} size="sm">
      {/* 폼 */}
      <div className="space-y-4">
          {/* 레벨 선택 (추가 모드이고 부모ID 없을 때만) */}
          {!editingCategory && !parentId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                레벨 *
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setLevel('major')}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    level === 'major'
                      ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  대분류
                </button>
                <button
                  onClick={() => setLevel('minor')}
                  className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                    level === 'minor'
                      ? 'bg-primary-600 dark:bg-primary-500 text-white border-primary-600 dark:border-primary-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  소분류
                </button>
              </div>
            </div>
          )}

          {/* 상위 카테고리 선택 (소분류일 때) */}
          {level === 'minor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                상위 카테고리 *
              </label>
              <select
                value={selectedParentId || ''}
                onChange={(e) => setSelectedParentId(e.target.value || null)}
                disabled={!!parentId} // 부모ID가 지정되어 있으면 변경 불가
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
              >
                <option value="">선택하세요</option>
                {majorCategories.map((major) => (
                  <option key={major.id} value={major.id}>
                    {major.icon} {major.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 이름 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              이름 *
            </label>
            <input
              type="text"
              inputMode="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 식비"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
          </div>

          {/* 이모지 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              아이콘 *
            </label>
            <EmojiPicker selected={icon} onSelect={setIcon} />
          </div>

          {/* 정렬 순서 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              정렬 순서
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              숫자가 작을수록 위에 표시됩니다
            </p>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
    </Modal>
  )
}
